import { makeStyles } from "@material-ui/core"
import clsx from "clsx"
import { ReactNode } from "react"
import { colors } from "../../utils/theme"

const BORDER_COLOR = "rgba(0, 0, 0, 0.1)"
const MUTED_ROW_BACKGROUND = "rgba(0, 0, 0, 0.02)"

export type TableColumnKey = string

export interface GenericTableColumn {
  key: TableColumnKey
  displayText: string
  align?: "left" | "center" | "right"
  width?: number | string
  minWidth?: number | string
  /** Optional rich header cell value rendered via renderCell (e.g. Title+Subtitle). */
  headerValue?: GenericTableCellValue
  titleRenderTypeOverride?: string // Optional type override for custom rendering of column rendering
}

export interface GenericTableCellValue {
  type: string
  value: unknown
}

export type GenericTableRow = {
  id: string | number
  values: Record<TableColumnKey, GenericTableCellValue>
}

export interface GenericTableProps {
  columns: GenericTableColumn[]
  rows: GenericTableRow[]
  renderCell: (
    value: GenericTableCellValue,
    useSplitTableLayout?: boolean
  ) => ReactNode
  className?: string
  /**
   * Optional zero-based column index after which a vertical split border
   * should be rendered. Used for layouts that visually separate left/right
   * column groups (e.g. DBT diary tables).
   */
  splitAfterColumnIndex?: number
  /**
   * Optional titles for the left and right halves of a split table. When
   * provided together with splitAfterColumnIndex, an additional header row
   will be rendered above the normal header, with one cell spanning the left
   * group (indices 0..splitAfterColumnIndex) and one spanning the right group
   * (indices splitAfterColumnIndex+1..end).
   */
  groupHeaderTitles?: {
    left?: string
    right?: string
  }
}

const alignClassnameMap = {
  left: "alignLeft",
  center: "alignCenter",
  right: "alignRight",
} as const

/**
 * Renders a generic table using provided column definitions, row data, and a custom cell renderer.
 * Supports column-level alignment and width overrides, renders alternating muted body rows,
 * and applies consistent padding, typography, and border styling for both header and body cells.
 *
 * @param columns - Array of column definitions including display text, keys, widths, and alignment options.
 * @param rows - Data rows to render, each uniquely identified by an `id`.
 * @param renderCell - Callback used to render the content of each cell based on the row value and column key.
 * @param className - Optional additional class name applied to the table wrapper. May be used for custom styling.
 */
function GenericTable({
  columns,
  rows,
  renderCell,
  className,
  splitAfterColumnIndex,
  groupHeaderTitles,
}: GenericTableProps) {
  const classes = useStyles()

  return (
    <div className={clsx(classes.root, className)}>
      <table className={classes.table}>
        <thead>
          {typeof splitAfterColumnIndex === "number" && groupHeaderTitles && (
            <tr className={classes.headerRow}>
              {splitAfterColumnIndex + 1 > 0 && (
                <th
                  className={classes.headerGroupCell}
                  colSpan={splitAfterColumnIndex + 1}
                >
                  {groupHeaderTitles.left}
                </th>
              )}
              {columns.length - (splitAfterColumnIndex + 1) > 0 && (
                <th
                  className={classes.headerGroupCell}
                  colSpan={columns.length - (splitAfterColumnIndex + 1)}
                >
                  {groupHeaderTitles.right}
                </th>
              )}
            </tr>
          )}
          <tr className={classes.headerRow}>
            {columns.map((column, columnIndex) => (
              <th
                key={column.key}
                className={clsx(
                  classes.headerCell,
                  groupHeaderTitles && classes.headerCellWithGroupSpacing,
                  column.align && classes[alignClassnameMap[column.align]],
                  splitAfterColumnIndex === columnIndex && classes.splitColumn
                )}
                style={{ width: column.width, minWidth: column.minWidth }}
              >
                {column.headerValue ? (
                  renderCell(column.headerValue)
                ) : column.titleRenderTypeOverride ? (
                  renderCell(
                    {
                      type: column.titleRenderTypeOverride,
                      value: column.displayText,
                    },
                    splitAfterColumnIndex !== undefined
                  )
                ) : (
                  <p> {column.displayText} </p>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={row.id}
              className={clsx(
                classes.bodyRow,
                rowIndex % 2 === 0 && classes.mutedBodyRow
              )}
            >
              {columns.map((column, columnIndex) => (
                <td
                  key={`${row.id}-${column.key}`}
                  className={clsx(
                    classes.cell,
                    column.align && classes[alignClassnameMap[column.align]],
                    splitAfterColumnIndex === columnIndex && classes.splitColumn
                  )}
                  style={{ width: column.width, minWidth: column.minWidth }}
                >
                  <div
                    className={clsx(
                      classes.innerFlexDiv,
                      column.align && classes[alignClassnameMap[column.align]]
                    )}
                  >
                    {renderCell(
                      row.values[column.key],
                      splitAfterColumnIndex !== undefined
                    )}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const useStyles = makeStyles({
  root: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    border: `1px solid ${BORDER_COLOR}`,
    borderRadius: 12,
    overflowX: "auto",
    overflowY: "hidden",
    backgroundColor: colors.neutral.white,
    marginTop: 16,
    marginBottom: 16,
    WebkitOverflowScrolling: "touch",
  },
  table: {
    width: "100%",
    minWidth: 480,
    borderCollapse: "collapse",
    fontFamily: "Lato, sans-serif",
    "& tbody tr:last-of-type td": {
      borderBottom: 0,
    },
  },
  headerRow: {
    backgroundColor: colors.neutral.white,
  },
  headerCell: {
    fontSize: 16,
    lineHeight: "20px",
    fontWeight: 700,
    color: colors.neutral.denim,
    textAlign: "center" as const,
    borderBottom: `1px solid ${BORDER_COLOR}`,
    whiteSpace: "pre-line",
  },
  headerCellWithGroupSpacing: {
    paddingBottom: 8,
  },
  headerGroupCell: {
    fontSize: 16,
    lineHeight: "20px",
    fontWeight: 700,
    color: colors.neutral.denim,
    textAlign: "center" as const,
    borderBottom: "none",
    whiteSpace: "pre-line",
    paddingTop: 8,
  },
  bodyRow: {
    backgroundColor: colors.neutral.white,
  },
  mutedBodyRow: {
    backgroundColor: MUTED_ROW_BACKGROUND,
  },
  cell: {
    fontSize: 14,
    lineHeight: "16px",
    fontWeight: 400,
    color: colors.neutral.denim,
    padding: "16px 8px",
    borderBottom: `1px solid ${BORDER_COLOR}`,
    verticalAlign: "middle" as const,
  },
  splitColumn: {
    borderRight: `2px solid ${BORDER_COLOR}`,
  },
  alignLeft: {
    textAlign: "left" as const,
    justifyContent: "flex-start",
  },
  alignCenter: {
    textAlign: "center" as const,
    justifyContent: "center",
  },
  alignRight: {
    textAlign: "right" as const,
    justifyContent: "flex-end",
  },
  innerFlexDiv: {
    display: "flex",
    height: "100%",
    width: "100%",
  },
})

export default GenericTable
